import { useMemo, useState } from 'react';
import { formatValue, toNumber, type ValueFormat } from '../format';
import { BarChart, Funnel, LineChart } from './Charts';

interface ColumnDef {
  key: string;
  label?: string;
  format?: ValueFormat;
  align?: 'left' | 'right' | 'center';
  width?: number;
  flag?: { test: 'lt' | 'lte' | 'gt' | 'gte' | 'eq'; value: number; tone: 'warn' | 'bad' | 'good' };
}

function flagClass(column: ColumnDef, value: unknown): string {
  const numeric = column.format && column.format !== 'text' && column.format !== 'date';
  const base = numeric || column.align === 'right' ? 'right' : '';
  if (!column.flag) return base;
  const n = Number(value);
  if (Number.isNaN(n)) return base;
  const { test, value: threshold, tone } = column.flag;
  const hit =
    (test === 'lt' && n < threshold) ||
    (test === 'lte' && n <= threshold) ||
    (test === 'gt' && n > threshold) ||
    (test === 'gte' && n >= threshold) ||
    (test === 'eq' && n === threshold);
  return hit ? `${base} flag flag-${tone}`.trim() : base;
}

export function DataTable({
  rows,
  columns,
  totals,
  pageSize = 50,
}: {
  rows: Record<string, any>[];
  columns: ColumnDef[];
  totals?: Record<string, number>;
  pageSize?: number;
}) {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [shown, setShown] = useState(pageSize);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    const numeric = Boolean(column?.format && column.format !== 'text' && column.format !== 'date');
    const factor = sort.dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) =>
      numeric
        ? factor * (toNumber(a[sort.key]) - toNumber(b[sort.key]))
        : factor * String(a[sort.key] ?? '').localeCompare(String(b[sort.key] ?? ''), 'en-GB', { numeric: true }),
    );
  }, [rows, sort, columns]);

  if (!rows.length) return <p className="note">No rows returned for this table.</p>;

  const visible = sorted.slice(0, shown);

  return (
    <>
      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              {columns.map((c) => {
                const numeric = c.format && c.format !== 'text' && c.format !== 'date';
                const active = sort?.key === c.key;
                return (
                  <th
                    key={c.key}
                    className={numeric || c.align === 'right' ? 'right' : ''}
                    style={{ width: c.width, cursor: 'pointer' }}
                    onClick={() =>
                      setSort((prev) =>
                        prev?.key === c.key ? { key: c.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key: c.key, dir: 'desc' },
                      )
                    }
                    aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    title="Sort by this column"
                  >
                    {c.label ?? c.key}
                    {active ? (sort!.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c.key} className={flagClass(c, row[c.key])}>
                    {formatValue(row[c.key], c.format ?? 'text')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {totals && Object.keys(totals).length > 0 && (
            <tfoot>
              <tr>
                {columns.map((c, i) => (
                  <th key={c.key} className={i === 0 ? '' : 'right'}>
                    {i === 0 ? 'Total' : Object.prototype.hasOwnProperty.call(totals, c.key) ? formatValue(totals[c.key], c.format ?? 'integer') : ''}
                  </th>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {sorted.length > shown && (
        <button className="ghost small" style={{ marginTop: 8 }} onClick={() => setShown((n) => n + pageSize)}>
          Show {Math.min(pageSize, sorted.length - shown)} more of {sorted.length} rows
        </button>
      )}
    </>
  );
}

export function KpiRow({ items }: { items: any[] }) {
  if (!items?.length) return null;
  return (
    <div className="kpi-row">
      {items.map((item, i) => (
        <div className="kpi" key={`${item.label}-${i}`}>
          <div className="label">{item.label}</div>
          <div className="value">{formatValue(item.value, item.format ?? 'integer')}</div>
          {item.rate !== null && item.rate !== undefined ? (
            <div className="sub">{formatValue(item.rate, 'percent')} conversion</div>
          ) : item.help ? (
            <div className="sub">{item.help}</div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Tiny markdown subset, matching the server-side renderer. */
function MiniMarkdown({ text }: { text: string }) {
  const html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/\n/g, '<br/>');
  return <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }} dangerouslySetInnerHTML={{ __html: html }} />;
}

export function BlockRenderer({ block, data }: { block: any; data: Record<string, Record<string, any>[]> }) {
  switch (block.type) {
    case 'kpi':
      return (
        <section style={{ marginBottom: 16 }}>
          {block.title && <h2 style={{ fontSize: 13, fontWeight: 620, margin: '0 0 10px', color: 'var(--ink-2)' }}>{block.title}</h2>}
          <KpiRow items={block.computed?.items ?? []} />
          {block.note && <p className="note">{block.note}</p>}
        </section>
      );

    case 'table':
      return (
        <section className="card" style={{ marginBottom: 16 }}>
          {block.title && <h3>{block.title}</h3>}
          <DataTable
            rows={data[block.source] ?? []}
            columns={block.computed?.columns ?? block.columns ?? []}
            totals={block.computed?.totals}
          />
          {block.note && <p className="note">{block.note}</p>}
        </section>
      );

    case 'bar':
      return (
        <section className="card" style={{ marginBottom: 16 }}>
          {block.title && <h3>{block.title}</h3>}
          <BarChart
            points={block.computed?.points ?? []}
            series={block.computed?.series ?? []}
            stacked={block.stacked}
            xLabel={block.x}
            format={(block as any).format}
          />
          {block.note && <p className="note">{block.note}</p>}
        </section>
      );

    case 'line':
      return (
        <section className="card" style={{ marginBottom: 16 }}>
          {block.title && <h3>{block.title}</h3>}
          <LineChart
            points={block.computed?.points ?? []}
            series={block.computed?.series ?? []}
            xLabel={block.x}
            format={(block as any).format}
          />
          {block.note && <p className="note">{block.note}</p>}
        </section>
      );

    case 'funnel':
      return (
        <section className="card" style={{ marginBottom: 16 }}>
          {block.title && <h3>{block.title}</h3>}
          <Funnel stages={block.computed?.stages ?? []} />
          {block.note && <p className="note">{block.note}</p>}
        </section>
      );

    case 'text':
      return (
        <section className="card" style={{ marginBottom: 16 }}>
          {block.title && <h3>{block.title}</h3>}
          <MiniMarkdown text={block.markdown ?? ''} />
        </section>
      );

    default:
      return (
        <section className="card" style={{ marginBottom: 16 }}>
          <p className="note">Unsupported block type “{block.type}”. Update the report definition.</p>
        </section>
      );
  }
}
